<template>
  <el-dialog
    :visible="visible"
    :show-close="true"
    :close-on-click-modal="false"
    width="640px"
    append-to-body
    custom-class="diagnostic-dialog"
    @close="onClose"
    @update:visible="onUpdateVisible"
  >
    <div slot="title" class="diag-title">
      <span class="diag-title__icon" :class="`diag-title__icon--${severity}`" aria-hidden="true">
        <happily-icon :name="severityIcon" :size="18" />
      </span>
      <div class="diag-title__text">
        <div class="diag-title__primary">{{ headline }}</div>
        <div class="diag-title__sub">{{ subhead }}</div>
      </div>
    </div>

    <div class="diag-body">
      <!-- LIKELY CAUSES -->
      <section v-if="likelyCauses.length" class="diag-section">
        <div class="aq-form-label diag-eyebrow">Likely causes</div>
        <ul class="diag-causes">
          <li v-for="(c, i) in likelyCauses" :key="i" class="diag-cause">
            <div class="diag-cause__body">
              <div class="diag-cause__title">{{ c.title }}</div>
              <div class="diag-cause__detail">{{ c.detail }}</div>
            </div>
          </li>
        </ul>
      </section>

      <!-- RESOLVED QUERY -->
      <section class="diag-section">
        <div class="aq-form-label diag-eyebrow">What we asked the source</div>
        <div class="diag-query">
          <div class="diag-query__row">
            <span class="diag-query__key">Source</span>
            <span class="diag-query__val diag-query__source">
              <AutomationToolkitMark
                :slug="toolkitSlug"
                :name="toolkitName"
                :logo-url="toolkitLogoUrl"
                size="sm"
              />
              <span>{{ toolkitName }}</span>
            </span>
          </div>
          <div class="diag-query__row">
            <span class="diag-query__key">Action</span>
            <span class="diag-query__val diag-query__mono">{{ actionSlug || '—' }}</span>
          </div>
          <div
            v-for="row in recipeParamRows"
            :key="row.key"
            class="diag-query__row"
          >
            <span class="diag-query__key">{{ row.key }}</span>
            <span class="diag-query__val diag-query__mono">{{ row.value }}</span>
          </div>
          <div v-if="dateSpanDays != null && dateRangeText" class="diag-query__row">
            <span class="diag-query__key">Resolved to</span>
            <span class="diag-query__val">
              {{ dateRangeText }}
              <span class="diag-query__hint">({{ dateSpanDays }} day{{ dateSpanDays === 1 ? '' : 's' }})</span>
            </span>
          </div>
          <div v-if="filtersList.length" class="diag-query__row">
            <span class="diag-query__key">Resolved filters</span>
            <div class="diag-query__val diag-query__filters">
              <span v-for="(f, i) in filtersList" :key="i" class="diag-filter-chip">{{ f }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- FIELD COMPARISON (only when aggregation mismatch detected) -->
      <section v-if="hasAggregationMismatch" class="diag-section">
        <div class="aq-form-label diag-eyebrow">Field comparison</div>
        <div class="diag-fieldcmp">
          <div class="diag-fieldcmp__col">
            <div class="diag-fieldcmp__head">
              <span class="diag-fieldcmp__head-label">We tried to {{ aggregationName || 'aggregate' }}</span>
            </div>
            <div class="diag-fieldcmp__value diag-fieldcmp__value--mono">{{ valueField || '—' }}</div>
            <div class="diag-fieldcmp__hint">From the recipe's post-processing</div>
          </div>
          <div class="diag-fieldcmp__arrow" aria-hidden="true">→</div>
          <div class="diag-fieldcmp__col">
            <div class="diag-fieldcmp__head">
              <span class="diag-fieldcmp__head-label">Source row contained</span>
            </div>
            <div v-if="firstSampleRow" class="diag-fieldcmp__value">
              <span class="diag-fieldcmp__sample-label">{{ firstSampleRow.label }}</span>
              <span class="diag-fieldcmp__sample-sep">·</span>
              <span class="diag-fieldcmp__sample-value">{{ firstSampleRow.value }}</span>
            </div>
            <div v-else class="diag-fieldcmp__value diag-fieldcmp__value--muted">No sample row available</div>
            <div class="diag-fieldcmp__hint">From {{ toolkitName || 'the source' }}</div>
          </div>
        </div>
        <div class="diag-fieldcmp__explain">
          The two don't line up, so {{ aggregationName || 'the aggregation' }} returns 0 even though
          {{ recordsCounted }} record{{ recordsCounted === 1 ? '' : 's' }} came back. Edit the prompt to
          name the exact metric you want (e.g. "sum sessions") so re-interpretation aligns the field
          with what the source returns.
        </div>
      </section>

      <!-- WHAT CAME BACK -->
      <section class="diag-section">
        <div class="aq-form-label diag-eyebrow">What came back</div>
        <div class="diag-result">
          <div class="diag-result__head">
            <span class="diag-result__count">{{ recordsCounted }} {{ recordsCounted === 1 ? 'record' : 'records' }}</span>
            <span class="diag-result__sep">·</span>
            <span class="diag-result__value">value {{ valueText }}</span>
          </div>
          <ul v-if="sampleRecords.length" class="diag-sample-list">
            <li v-for="(r, i) in sampleRecords" :key="i" class="diag-sample-row">
              <span class="diag-sample-label">{{ r.label }}</span>
              <span class="diag-sample-value">{{ r.value }}</span>
            </li>
          </ul>
          <div v-else class="diag-result__empty">
            <happily-icon name="info" :size="14" />
            <span>The source matched nothing for these parameters.</span>
          </div>
        </div>
      </section>

      <!-- WARNINGS RELAY -->
      <section v-if="rawWarnings.length" class="diag-section">
        <div class="aq-form-label diag-eyebrow">Warnings from source</div>
        <ul class="diag-warnings">
          <li v-for="(w, i) in rawWarnings" :key="i" class="diag-warning">
            <happily-icon name="alert-triangle" :size="14" />
            <span>{{ w }}</span>
          </li>
        </ul>
      </section>

      <!-- UNMATCHED OWNERS -->
      <section v-if="unmatchedOwners.length" class="diag-section">
        <div class="aq-form-label diag-eyebrow">Owners we couldn't match</div>
        <ul class="diag-unmatched">
          <li v-for="email in unmatchedOwners" :key="email" class="diag-unmatched-row">
            <happily-icon name="alert-circle" :size="13" />
            <span>{{ email }}</span>
          </li>
        </ul>
        <div class="diag-unmatched-hint">
          {{ toolkitName }} couldn't find a user with these emails. Check that the address in {{ toolkitName }} matches the email used here.
        </div>
      </section>
    </div>

    <div slot="footer" class="diag-footer">
      <div class="diag-footer__actions">
        <button type="button" class="diag-action" @click="onEditPrompt">
          <happily-icon name="edit" :size="13" />
          <span>Edit prompt</span>
        </button>
        <button type="button" class="diag-action" @click="onSwitchSource">
          <happily-icon name="refresh" :size="13" />
          <span>Try a different source</span>
        </button>
        <button
          v-if="showReconnectAction"
          type="button"
          class="diag-action diag-action--accent"
          @click="onReconnect"
        >
          <happily-icon name="refresh" :size="13" />
          <span>Reconnect {{ toolkitName }}</span>
        </button>
      </div>
      <el-button class="diag-footer__close" @click="onClose">Close</el-button>
    </div>
  </el-dialog>
</template>

<script>
import AutomationToolkitMark from './AutomationToolkitMark.vue'
import { formatRecipeParams } from '@/services/recipeParams'

export default {
  name: 'AutomationDiagnosticDialog',
  components: { AutomationToolkitMark },
  props: {
    visible: { type: Boolean, default: false },
    recipe: { type: Object, default: null },
    preview: { type: Object, default: null },
    toolkitSlug: { type: String, default: '' },
    toolkitName: { type: String, default: '' },
    toolkitLogoUrl: { type: String, default: '' },
    unit: { type: String, default: '' },
  },
  computed: {
    sourceSummary() {
      return (this.preview && this.preview.source_summary) || {}
    },
    recordsCounted() {
      const n = this.sourceSummary.records_counted
      return typeof n === 'number' ? n : 0
    },
    sampleRecords() {
      return this.sourceSummary.sample_records || []
    },
    rawWarnings() {
      return (this.preview && this.preview.warnings) || []
    },
    unmatchedOwners() {
      return (this.preview && this.preview.unmatched_owners) || []
    },
    filtersList() {
      return this.sourceSummary.filters_applied || []
    },
    actionSlug() {
      return this.recipe && this.recipe.action_slug ? this.recipe.action_slug : ''
    },
    aggregationText() {
      const pp = this.recipe && this.recipe.post_processing
      if (!pp) return '—'
      const base = pp.aggregation || '—'
      if (pp.value_field) return `${base} on ${pp.value_field}`
      return base
    },
    aggregationName() {
      const pp = this.recipe && this.recipe.post_processing
      return (pp && pp.aggregation) || ''
    },
    valueField() {
      const pp = this.recipe && this.recipe.post_processing
      return (pp && pp.value_field) || ''
    },
    requestedMetric() {
      // Surfaces whichever metric the action_args asked for, so the
      // "field comparison" panel can spell out the drift between the
      // metric requested and the field the post-processing tried to sum.
      const args = (this.recipe && this.recipe.action_args) || {}
      const metrics = args.metrics
      if (Array.isArray(metrics) && metrics.length) return metrics.join(', ')
      if (typeof args.metric === 'string') return args.metric
      if (typeof args.metric_name === 'string') return args.metric_name
      return ''
    },
    firstSampleRow() {
      return this.sampleRecords[0] || null
    },
    recipeParamRows() {
      return formatRecipeParams(this.recipe, this.toolkitSlug)
    },
    dateRangeText() {
      const dr = this.sourceSummary.date_range
      if (!dr || !dr.start || !dr.end) return ''
      return `${this.formatDate(dr.start)} – ${this.formatDate(dr.end)}`
    },
    dateSpanDays() {
      const dr = this.sourceSummary.date_range
      if (!dr || !dr.start || !dr.end) return null
      const start = new Date(dr.start).getTime()
      const end = new Date(dr.end).getTime()
      if (!Number.isFinite(start) || !Number.isFinite(end)) return null
      const days = Math.round((end - start) / 86400000)
      return days >= 0 ? days : null
    },
    valueText() {
      if (!this.preview || this.preview.value == null) return '—'
      const formatted = Number(this.preview.value).toLocaleString(undefined, { maximumFractionDigits: 2 })
      return this.unit ? `${formatted} ${this.unit}` : formatted
    },
    hasAuthWarning() {
      return this.rawWarnings.some((w) => /auth|expired|reconnect|token|unauthor/i.test(String(w)))
    },
    /**
     * Records came back, but the aggregation summed to 0 (or nothing).
     * High-signal mismatch: the value_field probably doesn't match the
     * shape of the rows the source returned. We treat this as its own
     * severity tier because the fix is specific and the heuristic is
     * confident.
     */
    hasAggregationMismatch() {
      if (!this.preview) return false
      if (this.recordsCounted <= 0) return false
      const v = this.preview.value
      return v == null || v === 0
    },
    severity() {
      if (this.hasAuthWarning) return 'auth'
      if (this.hasAggregationMismatch) return 'mismatch'
      if (this.recordsCounted === 0) return 'warn'
      if (this.rawWarnings.length) return 'warn'
      return 'info'
    },
    severityIcon() {
      if (this.severity === 'auth') return 'lock'
      if (this.severity === 'mismatch') return 'alert-triangle'
      if (this.severity === 'warn') return 'alert-triangle'
      return 'info'
    },
    headline() {
      if (this.hasAuthWarning) return 'Authentication needs attention'
      if (this.hasAggregationMismatch) return 'Records came back, but the sum is 0'
      if (this.recordsCounted === 0) return 'Source returned no records'
      if (this.rawWarnings.length) return 'Source returned with warnings'
      return 'Diagnose result'
    },
    subhead() {
      return `Inspect what we asked ${this.toolkitName || 'the source'} and what came back.`
    },
    showReconnectAction() {
      return this.hasAuthWarning
    },
    /**
     * Heuristic likely-cause list. We lead with the highest-signal cause:
     * auth issues short-circuit other guesses since nothing else matters
     * until the credential is fixed. Otherwise we layer narrow-filter and
     * short-window guesses based on the resolved query the recipe produced.
     */
    likelyCauses() {
      const causes = []
      if (this.hasAuthWarning) {
        causes.push({
          title: 'Authentication may have expired',
          detail: `Reconnect ${this.toolkitName} from Manage connected apps and try again.`,
        })
        return causes
      }

      if (this.hasAggregationMismatch) {
        const field = this.valueField || 'the configured field'
        const aggLabel = this.aggregationName
          ? this.aggregationName.charAt(0).toUpperCase() + this.aggregationName.slice(1)
          : 'Aggregation'
        const metric = this.requestedMetric
        const metricBit = metric ? ` The source was asked for ${metric}, but ` : ''
        causes.push({
          title: `${aggLabel} couldn't pull a value from any of the ${this.recordsCounted} record${this.recordsCounted === 1 ? '' : 's'}`,
          detail: `${metricBit}we tried to ${this.aggregationName || 'aggregate'} \`${field}\` across the returned rows and got nothing. The field path probably doesn't match the shape of the response. Re-interpret with the metric name spelled out (for example, "sum sessions" or "sum active users") and confirm the Key Result unit matches.`,
        })
        // No early return — unmatched_owners below can still apply.
      }

      if (this.unmatchedOwners.length) {
        const head = this.unmatchedOwners.slice(0, 3).join(', ')
        const more = this.unmatchedOwners.length > 3 ? `, +${this.unmatchedOwners.length - 3} more` : ''
        causes.push({
          title: `${this.unmatchedOwners.length} owner${this.unmatchedOwners.length > 1 ? 's' : ''} couldn't be matched in ${this.toolkitName}`,
          detail: `${head}${more}. Confirm the email matches the one used in ${this.toolkitName}.`,
        })
      }

      if (this.recordsCounted === 0) {
        const filterCount = this.filtersList.length
        if (filterCount > 0) {
          const shown = this.filtersList.slice(0, 3).join(', ')
          const more = filterCount > 3 ? '…' : ''
          causes.push({
            title: 'Filters may be too narrow',
            detail: `${filterCount} filter${filterCount > 1 ? 's are' : ' is'} being applied (${shown}${more}). Drop the most specific one or double-check spelling. Many sources match exactly and are case-sensitive.`,
          })
        }
        if (this.dateSpanDays != null && this.dateSpanDays <= 14) {
          causes.push({
            title: 'Date window may be too recent',
            detail: `The query covers ${this.dateSpanDays} day${this.dateSpanDays === 1 ? '' : 's'}. Some sources lag and matching records may not be present yet. Try widening to 60 or 90 days in the prompt.`,
          })
        }
        if (filterCount === 0 && (this.dateSpanDays == null || this.dateSpanDays > 14)) {
          causes.push({
            title: `${this.toolkitName} returned nothing for this query`,
            detail: 'No filters and a wide window. The action may target a different account, property, or workspace than expected. Try a different source or edit the prompt to name the specific account.',
          })
        }
      }

      return causes
    },
  },
  methods: {
    formatDate(iso) {
      if (!iso) return ''
      try {
        const d = new Date(iso)
        if (Number.isNaN(d.getTime())) return iso
        const now = new Date()
        const sameYear = d.getFullYear() === now.getFullYear()
        const opts = sameYear
          ? { month: 'short', day: 'numeric' }
          : { month: 'short', day: 'numeric', year: 'numeric' }
        return d.toLocaleDateString(undefined, opts)
      } catch {
        return iso
      }
    },
    onUpdateVisible(v) {
      this.$emit('update:visible', v)
    },
    onClose() {
      this.$emit('update:visible', false)
      this.$emit('close')
    },
    onEditPrompt() {
      this.$emit('edit-prompt')
      this.onClose()
    },
    onSwitchSource() {
      this.$emit('switch-source')
      this.onClose()
    },
    onReconnect() {
      this.$emit('reconnect')
      this.onClose()
    },
  },
}
</script>

<style scoped>
/* ===== Title ===== */
.diag-title {
  display: flex;
  align-items: center;
  gap: 14px;
}
.diag-title__icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.diag-title__icon--info {
  background: #f2ecfb;
  color: #6c30cc;
}
.diag-title__icon--warn {
  background: #fff7e4;
  color: #a4720a;
}
.diag-title__icon--auth {
  background: #fdecec;
  color: #b22a2a;
}
.diag-title__icon--mismatch {
  background: #fff7e4;
  color: #a4720a;
}
.diag-title__primary {
  font-family: 'Lato', sans-serif;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.005em;
  color: #1f1f1f;
  line-height: 1.2;
}
.diag-title__sub {
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.4;
  color: #8a8a8a;
  margin-top: 4px;
}

/* ===== Body ===== */
.diag-body {
  padding: 4px 4px 0;
  display: flex;
  flex-direction: column;
  gap: 22px;
}
.diag-section { }
.diag-eyebrow {
  margin: 0 0 10px;
}

/* ===== Likely causes ===== */
.diag-causes {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.diag-cause {
  padding: 12px 14px;
  background: #fffcf3;
  border: 1px solid #f5dfa2;
  border-radius: 10px;
}
.diag-cause__title {
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: -0.005em;
  color: #1f1f1f;
  line-height: 1.3;
}
.diag-cause__detail {
  margin-top: 4px;
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #4a4a4a;
  line-height: 1.5;
}

/* ===== Resolved query ===== */
.diag-query {
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: #fafbfd;
  border: 1px solid #eef0f5;
  border-radius: 10px;
  padding: 12px 14px;
}
.diag-query__row {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}
.diag-query__key {
  flex: 0 0 96px;
  font-family: 'Lato', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #8a8a8a;
  padding-top: 2px;
}
.diag-query__val {
  flex: 1;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: #1f1f1f;
  font-weight: 600;
  line-height: 1.45;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.diag-query__source {
  font-weight: 700;
}
.diag-query__hint {
  font-weight: 500;
  color: #8a8a8a;
}
.diag-query__mono {
  font-family: 'JetBrains Mono', 'Menlo', 'Monaco', monospace;
  font-size: 12px;
  background: #f2f3f5;
  padding: 1px 6px;
  border-radius: 5px;
  border: 1px solid #e6e7ec;
  color: #4a4a4a;
}
.diag-query__filters {
  gap: 6px;
}
.diag-filter-chip {
  display: inline-flex;
  align-items: center;
  background: #ffffff;
  border: 1px solid #e2e4ea;
  border-radius: 999px;
  padding: 2px 9px;
  font-size: 11px;
  font-weight: 600;
  color: #4a4a4a;
  line-height: 1.45;
}

/* ===== Field comparison ===== */
.diag-fieldcmp {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: stretch;
  gap: 12px;
  background: #fffcf3;
  border: 1px solid #f5dfa2;
  border-radius: 10px;
  padding: 14px;
}
.diag-fieldcmp__col {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}
.diag-fieldcmp__head {
  font-family: 'Lato', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #a4720a;
}
.diag-fieldcmp__value {
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #1f1f1f;
  line-height: 1.35;
  word-break: break-word;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
}
.diag-fieldcmp__value--mono {
  font-family: 'JetBrains Mono', 'Menlo', 'Monaco', monospace;
  font-size: 12px;
  background: #ffffff;
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid #f0d99a;
  color: #4a4a4a;
  display: inline-flex;
  width: fit-content;
  max-width: 100%;
  white-space: pre-wrap;
}
.diag-fieldcmp__value--muted {
  color: #8a8a8a;
  font-weight: 500;
  font-style: italic;
}
.diag-fieldcmp__sample-label {
  font-weight: 700;
  color: #1f1f1f;
}
.diag-fieldcmp__sample-sep {
  color: #c9ccd4;
  font-weight: 400;
}
.diag-fieldcmp__sample-value {
  font-weight: 700;
  color: #6c30cc;
}
.diag-fieldcmp__hint {
  font-family: 'Lato', sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: #8a8a8a;
  line-height: 1.4;
}
.diag-fieldcmp__arrow {
  font-size: 18px;
  font-weight: 700;
  color: #c8a25a;
  display: flex;
  align-items: center;
  justify-content: center;
}
.diag-fieldcmp__explain {
  margin-top: 10px;
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.55;
  color: #6a4a02;
}

@media (max-width: 560px) {
  .diag-fieldcmp {
    grid-template-columns: 1fr;
  }
  .diag-fieldcmp__arrow {
    transform: rotate(90deg);
    justify-content: flex-start;
  }
}

/* ===== Result ===== */
.diag-result {
  background: #ffffff;
  border: 1px solid #eef0f5;
  border-radius: 10px;
  padding: 12px 14px;
}
.diag-result__head {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #1f1f1f;
}
.diag-result__sep {
  color: #c9ccd4;
}
.diag-result__value {
  color: #4a4a4a;
  font-weight: 600;
}
.diag-sample-list {
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.diag-sample-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 10px;
  background: #fafbfd;
  border: 1px solid #eef0f5;
  border-radius: 8px;
  font-family: 'Lato', sans-serif;
  font-size: 12px;
}
.diag-sample-label {
  color: #1f1f1f;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.diag-sample-value {
  color: #4a4a4a;
  font-weight: 600;
  flex-shrink: 0;
}
.diag-result__empty {
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: #fafbfd;
  border: 1px dashed #d8dadd;
  border-radius: 8px;
  color: #6f6f6f;
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  font-weight: 500;
}

/* ===== Warnings ===== */
.diag-warnings {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.diag-warning {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 12px;
  background: #fff7e4;
  border: 1px solid #f5dfa2;
  border-radius: 8px;
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
  color: #6a4a02;
}
.diag-warning .happily-icon {
  margin-top: 1px;
  color: #a4720a;
  flex-shrink: 0;
}

/* ===== Unmatched owners ===== */
.diag-unmatched {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.diag-unmatched-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  background: #fdecec;
  border: 1px solid #f2c4c4;
  border-radius: 8px;
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #8a2a2a;
}
.diag-unmatched-hint {
  margin-top: 8px;
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #6f6f6f;
  line-height: 1.5;
}

/* ===== Footer ===== */
.diag-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.diag-footer__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.diag-action {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #ffffff;
  border: 1px solid #e2e4ea;
  border-radius: 8px;
  height: 32px;
  padding: 0 12px;
  font-family: 'Lato', sans-serif;
  font-weight: 700;
  font-size: 12px;
  color: #4a4a4a;
  cursor: pointer;
  transition: background 140ms ease, color 140ms ease,
              border-color 140ms ease, box-shadow 140ms ease;
}
.diag-action:hover {
  background: #f2ecfb;
  color: #6c30cc;
  border-color: #d8c6f0;
  box-shadow: 0 1px 4px rgba(108, 48, 204, 0.08);
}
.diag-action--accent {
  background: #6c30cc;
  border-color: #6c30cc;
  color: #ffffff;
}
.diag-action--accent:hover {
  background: #522698;
  border-color: #522698;
  color: #ffffff;
  box-shadow: 0 2px 8px rgba(108, 48, 204, 0.18);
}
.diag-footer__close {
  height: 32px;
  border-radius: 8px;
  font-weight: 700;
  font-family: 'Lato', sans-serif;
  border: 1px solid #e2e4ea;
  background: #ffffff;
  color: #4a4a4a;
}
.diag-footer__close:hover {
  background: #fafbfc;
  border-color: #d8dadd;
}
</style>

<style>
/* Non-scoped chrome alignment matches the sibling dialogs. */
.diagnostic-dialog .el-dialog__header {
  padding: 22px 28px 14px;
}
.diagnostic-dialog .el-dialog__body {
  padding: 6px 28px 16px;
}
.diagnostic-dialog .el-dialog__footer {
  padding: 18px 28px 20px;
  border-top: 1px solid #eef0f5;
}
.diagnostic-dialog .el-dialog__title {
  display: none;
}
.diagnostic-dialog .el-dialog__headerbtn {
  top: 22px;
  right: 22px;
}
</style>
